/**
 * Timestamps that reach the UI come from two writers with two conventions:
 * application code writes `new Date().toISOString()` ("…T08:53:16.000Z", explicitly
 * UTC), and SQLite column defaults write `datetime('now')` ("2026-08-10 08:53:16",
 * UTC by definition but carrying no marker to say so).
 *
 * `new Date()` reads the second form as LOCAL time, so in Jerusalem every
 * SQLite-stamped row rendered three hours in the past: the errors table said
 * 11:53:16 and the notifications bell called the same row "3h ago".
 *
 * One place to fix it. Append the marker the string is missing and every reader
 * agrees on what the instant is.
 */
export function parseServerDate(ts: string | null | undefined): Date | null {
  if (!ts) return null;
  const hasZone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(ts.trim());
  const d = new Date(hasZone ? ts : `${ts.trim().replace(" ", "T")}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Local-time display string for a server timestamp. Empty when unparseable. */
export function formatServerDate(ts: string | null | undefined, locale = "en-GB"): string {
  return parseServerDate(ts)?.toLocaleString(locale) ?? "";
}

/** "just now" / "5m ago" / "3h ago" / "2d ago". Empty when unparseable. */
export function timeAgo(ts: string | null | undefined): string {
  const d = parseServerDate(ts);
  if (!d) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
